<script setup lang="ts">
import type { CreateUserAttendeeInput, UserAttendeeView, VersionedFormResponses } from '#shared/types/user-attendees'
import type { GodmodeSlot } from '~/composables/useGodmodeSlots'
import { formatRijksregisternummer, validateRijksregisternummer } from '#shared/utils/rijksregisternummer'
import CheckoutAttendeeForm from '@/components/CheckoutAttendeeForm.vue'
import SavedAttendeePicker from '@/components/SavedAttendeePicker.vue'
import { useTicketTypes } from '@/composables/useTicketTypes'
import { useCartStore } from '@/stores/cart'
import { useGodmodeSlots } from '~/composables/useGodmodeSlots'
import { useUserAttendees } from '~/composables/useUserAttendees'
import { useUserAttendeeSaves } from '~/composables/useUserAttendeeSaves'

interface CampFormQuestion {
  id: string
  questionType: string
  label: string
  placeholder?: string
  helpText?: string
  isRequired: boolean
  options?: string[]
  conditionalRule?: unknown
  orderIndex: number
  content?: string
  titleSize?: string
  url?: string
  linkText?: string
  imageAlt?: string
}

interface CampForm {
  id: string
  name: string
  description?: string
  is_required: boolean
  order_index: number
  questions: CampFormQuestion[]
}

type FormAnswer = string | number | boolean | null | string[]

interface FormResponse {
  question_id: string
  answer: FormAnswer
}

interface DayOption {
  date: string
  label: string
  shortLabel: string
}

interface TicketTypeInfo {
  id: string
  name: string
  price: number
  availableQuantity: number | null
}

interface DayAvailabilityInfo {
  date: string
  available: number
  capacity: number
}

interface WeekTicketAvailabilityInfo {
  ticket_type_id: string
  available: number
}

interface Props {
  campForms: CampForm[]
  disabled?: boolean

  /** 1 = ticket-type selection only (cart step 1); 2 = attendee details (cart step 2, default) */
  phase?: 1 | 2

  /** When true, hides availability indicators ("vol", spot counts) and allows selecting any day.
   *  Used for invite-based bookings that bypass capacity. */
  ignoreAvailability?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  phase: 2,
  ignoreAvailability: false,
})

const cartStore = useCartStore()
const { getPublicTicketTypes } = useTicketTypes()
const { slots, createEmptyParticipant, createSlot } = useGodmodeSlots()
const authStore = useAuthStore()
const { createAttendee, updateAttendee, findByRijksregister } = useUserAttendees()
const { trackAttendeeSave } = useUserAttendeeSaves()

// Ticket types loaded from camp API
const weekTicketType = ref<TicketTypeInfo | null>(null)
const dayTicketType = ref<TicketTypeInfo | null>(null)
const loadingTicketTypes = ref(false)
const ticketTypeError = ref<string | null>(null)

// Real-time availability from /api/camps/[id]/availability
const availabilityData = ref<{
  weekTicketAvailability: WeekTicketAvailabilityInfo[]
  dayAvailability: DayAvailabilityInfo[]
} | null>(null)

// Spots left for week tickets (from availability API, falls back to ticket type available_quantity)
const weekSpotsLeft = computed<number | null>(() => {
  if (!weekTicketType.value)
    return null

  if (availabilityData.value) {
    const avail = availabilityData.value.weekTicketAvailability.find(
      w => w.ticket_type_id === weekTicketType.value!.id,
    )

    if (avail !== undefined)
      return avail.available
  }

  return weekTicketType.value.availableQuantity
})

const isWeekSoldOut = computed(() => {
  if (!weekTicketType.value)
    return true

  return weekSpotsLeft.value !== null && weekSpotsLeft.value <= 0
})

// Map of date → available spots for day tickets
const dayAvailabilityMap = computed<Record<string, number>>(() => {
  if (!availabilityData.value)
    return {}

  const map: Record<string, number> = {}
  for (const d of availabilityData.value.dayAvailability)
    map[d.date] = d.available

  return map
})

// Picker refs per slot
const pickerRefs = ref<Record<number, { openPicker: () => void }>>({})

// Available camp days (from start/end date)
const availableDays = computed<DayOption[]>(() => {
  if (!cartStore.camp_start_date || !cartStore.camp_end_date)
    return []

  const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za']
  const dayNamesFull = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag']
  const monthNames = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

  const parseDateUTC = (str: string): [number, number, number] => {
    const part = str.includes('T') ? str.split('T')[0]! : str
    const [y, m, d] = part.split('-').map(Number)

    return [y!, m!, d!]
  }

  const [sy, sm, sd] = parseDateUTC(cartStore.camp_start_date)
  const [ey, em, ed] = parseDateUTC(cartStore.camp_end_date)
  const start = new Date(Date.UTC(sy, sm - 1, sd))
  const end = new Date(Date.UTC(ey, em - 1, ed))

  const days: DayOption[] = []
  const endTime = end.getTime()

  for (let t = start.getTime(); t <= endTime; t += 86400000) {
    const cur = new Date(t)
    const dateStr = cur.toISOString().split('T')[0]!
    const dayIdx = cur.getUTCDay()

    days.push({
      date: dateStr,
      label: `${dayNamesFull[dayIdx]} ${cur.getUTCDate()} ${monthNames[cur.getUTCMonth()]}`,
      shortLabel: `${dayNames[dayIdx]} ${cur.getUTCDate()}/${cur.getUTCMonth() + 1}`,
    })
  }

  return days
})

// Initialize from current cart
const initFromCart = () => {
  // If already initialized (e.g., set by CampBookingDialog), skip
  if (slots.value.length > 0)
    return

  const weekQty = cartStore.items.filter(i => i.ticket_type === 'week').reduce((s, i) => s + i.quantity, 0)

  // Separate participants: those without attendance_date are week-ticket participants;
  // those with attendance_date are day-ticket participants.
  const weekParticipants = cartStore.participants.filter(p => !p.attendance_date)
  const dayParticipants = cartStore.participants.filter(p => !!p.attendance_date)

  // Group day participants back into per-child slots.
  // tempId for day participants is `${slotId}-${YYYY-MM-DD}` where the date is always
  // 10 chars, so we strip the trailing 11 chars ("-YYYY-MM-DD") to recover the slot key.
  const daySlotMap = new Map<string, { dates: string[], participant: typeof cartStore.participants[0] }>()
  for (const p of dayParticipants) {
    const slotKey = p.tempId.length > 11 ? p.tempId.slice(0, -11) : p.tempId
    if (!daySlotMap.has(slotKey))
      daySlotMap.set(slotKey, { dates: [], participant: p })
    if (p.attendance_date)
      daySlotMap.get(slotKey)!.dates.push(p.attendance_date)
  }

  // Fallback: day items exist but no day participants were persisted.
  // Reconstruct a single day slot from the aggregated day_tickets data.
  if (daySlotMap.size === 0 && cartStore.items.some(i => i.ticket_type === 'day')) {
    const dayItem = cartStore.items.find(i => i.ticket_type === 'day')
    if (dayItem?.day_tickets?.length) {
      const fallbackParticipant = weekParticipants[weekQty] ?? cartStore.participants[0]

      daySlotMap.set('fallback-0', {
        dates: dayItem.day_tickets.map(d => d.date),
        participant: fallbackParticipant ?? { tempId: '', first_name: '', last_name: '', date_of_birth: '', rijksregisternummer: '' },
      })
    }
  }

  const count = Math.max(1, weekQty + daySlotMap.size)

  slots.value = Array.from({ length: count }, (_, i) => {
    const slot = createSlot(i === 0)
    if (i >= weekQty)
      slot.type = 'day'

    return slot
  })

  // Populate week slots from week participants
  weekParticipants.forEach((p, i) => {
    if (i >= weekQty || i >= slots.value.length)
      return
    const slot = slots.value[i]!

    Object.assign(slot.participant, {
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      rijksregisternummer: p.rijksregisternummer || '',
      date_of_birth: p.date_of_birth || '',
      gender: p.gender || '',
      medical_info: p.medical_info || '',
      allergies: p.allergies || '',
      emergency_contact_name: p.emergency_contact_name || '',
      emergency_contact_phone: p.emergency_contact_phone || '',
      form_responses: (p.form_responses as FormResponse[]) || [],
    })
    if (p.first_name) {
      slot.newAttendeeSelected = true
      slot.isExpanded = true
    }
  })

  // Populate day slots: restore selectedDays and participant data
  let daySlotIdx = weekQty
  for (const { dates, participant: p } of daySlotMap.values()) {
    if (daySlotIdx >= slots.value.length)
      break
    const slot = slots.value[daySlotIdx]!

    slot.selectedDays = [...dates].sort()
    Object.assign(slot.participant, {
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      rijksregisternummer: p.rijksregisternummer || '',
      date_of_birth: p.date_of_birth || '',
      gender: p.gender || '',
      medical_info: p.medical_info || '',
      allergies: p.allergies || '',
      emergency_contact_name: p.emergency_contact_name || '',
      emergency_contact_phone: p.emergency_contact_phone || '',
      form_responses: (p.form_responses as FormResponse[]) || [],
    })
    if (p.first_name) {
      slot.newAttendeeSelected = true
      slot.isExpanded = true
    }
    daySlotIdx++
  }
}

// Sync cart store from current slot state
const syncCartFromSlots = () => {
  const wt = weekTicketType.value
  const dt = dayTicketType.value

  const weekSlots = slots.value.filter(s => s.type === 'week')
  const daySlots = slots.value.filter(s => s.type === 'day' && s.selectedDays.length > 0)

  const newItems: typeof cartStore.items = []

  if (weekSlots.length > 0 && wt) {
    newItems.push({
      ticket_type_id: wt.id,
      ticket_type_name: wt.name,
      ticket_type: 'week',
      price: wt.price,
      quantity: weekSlots.length,
      max_capacity: 999,
      available_spots: 999,
    })
  }

  if (daySlots.length > 0 && dt) {
    // Aggregate date → count across all day slots
    const dayCountMap: Record<string, number> = {}
    for (const slot of daySlots) {
      for (const date of slot.selectedDays) {
        dayCountMap[date] = (dayCountMap[date] || 0) + 1
      }
    }

    const dayTickets = Object.entries(dayCountMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, quantity]) => ({ date, quantity }))

    const totalDaySlots = daySlots.reduce((sum, s) => sum + s.selectedDays.length, 0)

    if (dayTickets.length > 0) {
      newItems.push({
        ticket_type_id: dt.id,
        ticket_type_name: dt.name,
        ticket_type: 'day',
        price: dt.price,
        quantity: totalDaySlots,
        max_capacity: 999,
        available_spots: 999,
        day_tickets: dayTickets,
      })
    }
  }

  // Build participants array: week slots first, then day slots (one per selected day)
  const newParticipants: typeof cartStore.participants = []

  for (const slot of weekSlots) {
    newParticipants.push({
      tempId: slot.id,
      user_attendee_id: slot.savedAttendee?.id,
      no_rrn: slot.noRrn || undefined,
      ...slot.participant,
    })
  }

  for (const slot of daySlots) {
    for (const date of [...slot.selectedDays].sort()) {
      newParticipants.push({
        tempId: `${slot.id}-${date}`,
        attendance_date: date,
        user_attendee_id: slot.savedAttendee?.id,
        no_rrn: slot.noRrn || undefined,
        ...slot.participant,
      })
    }
  }

  cartStore.$patch(state => {
    state.items = newItems
    state.participants = newParticipants
  })
}

// Participant count control
const setParticipantCount = (count: number) => {
  const n = Math.max(1, Math.min(20, count))
  if (n > slots.value.length) {
    while (slots.value.length < n) {
      const slot = createSlot(false)
      if (isWeekSoldOut.value && dayTicketType.value)
        slot.type = 'day'
      slots.value.push(slot)
    }
  }
  else if (n < slots.value.length) {
    slots.value.splice(n)
  }
  syncCartFromSlots()
}

const updateRrn = (slotIdx: number, value: string) => {
  const slot = slots.value[slotIdx]
  if (!slot)
    return
  const digits = value.replace(/\D/g, '').slice(0, 11)
  let formatted = ''
  if (digits.length > 0)
    formatted += digits.slice(0, 2)
  if (digits.length >= 3)
    formatted += `.${digits.slice(2, 4)}`
  if (digits.length >= 5)
    formatted += `.${digits.slice(4, 6)}`
  if (digits.length >= 7)
    formatted += `-${digits.slice(6, 9)}`
  if (digits.length >= 10)
    formatted += `.${digits.slice(9, 11)}`

  slot.participant.rijksregisternummer = formatted

  if (digits.length === 11) {
    const result = validateRijksregisternummer(formatted)
    if (result.isValid && result.birthDate) {
      const d = result.birthDate

      slot.participant.date_of_birth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      if (!slot.participant.gender && result.gender) {
        slot.participant.gender = result.gender === 'male' ? 'jongen' : result.gender === 'female' ? 'meisje' : ''
      }
    }
  }

  syncCartFromSlots()
}

// Attendee picker handlers per slot
const onAttendeeSelected = (slotIdx: number, attendee: UserAttendeeView) => {
  const slot = slots.value[slotIdx]
  if (!slot)
    return
  slot.savedAttendee = attendee
  slot.newAttendeeSelected = false
  Object.assign(slot.participant, {
    first_name: attendee.firstName || '',
    last_name: attendee.lastName || '',
    date_of_birth: attendee.dateOfBirth || '',
    gender: attendee.gender || '',
    medical_info: attendee.medicalInfo || '',
    allergies: attendee.allergies || '',
    emergency_contact_name: attendee.emergencyContactName || '',
    emergency_contact_phone: attendee.emergencyContactPhone || '',
  })
  updateRrn(slotIdx, attendee.rijksregisternummer || '')

  // Populate form responses from saved attendee data
  if (props.campForms?.length > 0 && attendee.savedFormResponses) {
    const formResponses: FormResponse[] = []

    props.campForms.forEach(form => {
      const savedFormData = attendee.savedFormResponses?.[form.id]
      if (savedFormData?.responses) {
        savedFormData.responses.forEach((response: { questionId: string, answer: FormAnswer }) => {
          formResponses.push({
            question_id: response.questionId,
            answer: response.answer as FormAnswer,
          })
        })
      }
    })
    slot.participant.form_responses = formResponses
  }

  syncCartFromSlots()

  // Expand so camp forms are immediately visible
  slot.isExpanded = true
}

const onAttendeeClear = (slotIdx: number) => {
  const slot = slots.value[slotIdx]
  if (!slot)
    return
  slot.savedAttendee = null
  slot.newAttendeeSelected = false
  slot.participant = createEmptyParticipant()
  syncCartFromSlots()
}

const startNewAttendee = (slotIdx: number) => {
  onAttendeeClear(slotIdx)

  const slot = slots.value[slotIdx]
  if (slot) {
    slot.newAttendeeSelected = true
    slot.isExpanded = true
  }
}

// ─── Auto-save new attendees to the user's profile ──────────────────────────
// When a logged-in user fills in a new attendee with a valid rijksregisternummer,
// persist it to their saved attendees and link it back to the slot. This keeps
// the booking participant's user_attendee_id populated, which fiscal certificates
// rely on to resolve the child's rijksregisternummer.
const autoSavedSlotIds = new Set<string>()
const autoSaveTimers = new Map<string, ReturnType<typeof setTimeout>>()

// Debounced timers for persisting extra form responses to already-saved attendees.
const formSaveTimers = new Map<string, ReturnType<typeof setTimeout>>()

const buildSavedFormResponses = (slot: GodmodeSlot): Record<string, VersionedFormResponses> | undefined => {
  if (!props.campForms?.length || !slot.participant.form_responses?.length)
    return undefined

  const responses: Record<string, VersionedFormResponses> = {}

  props.campForms.forEach(form => {
    const formQuestionIds = form.questions.map(q => q.id)

    const formResponses = slot.participant.form_responses.filter(
      r => formQuestionIds.includes(r.question_id) && r.answer !== null,
    )

    if (formResponses.length) {
      responses[form.id] = {
        version: new Date().toISOString(),
        formName: form.name,
        responses: formResponses.map(r => ({
          questionId: r.question_id,
          answer: r.answer as string | string[] | boolean | number,
          label: form.questions.find(q => q.id === r.question_id)?.label || '',
        })),
      }
    }
  })

  return Object.keys(responses).length > 0 ? responses : undefined
}

// Persist the slot's extra form responses to its already-saved attendee.
// Auto-save is a convenience, so failures are silently ignored.
const saveFormResponsesForSlot = async (slot: GodmodeSlot) => {
  if (!authStore.user || !slot.savedAttendee)
    return

  const savedFormResponses = buildSavedFormResponses(slot)
  if (!savedFormResponses)
    return

  try {
    const result = await updateAttendee(slot.savedAttendee.id, { savedFormResponses })
    if (result.success && result.data)
      slot.savedAttendee = result.data
  }
  catch {
    // Silently ignore — auto-save is a convenience, not critical.
  }
}

const autoSaveNewAttendee = async (slot: GodmodeSlot) => {
  if (!authStore.user || slot.savedAttendee || autoSavedSlotIds.has(slot.id))
    return
  if (!slot.participant.first_name || !slot.participant.last_name)
    return

  // Skip RRN validation when user indicated no RRN available
  if (!slot.noRrn) {
    const rrnValidation = validateRijksregisternummer(slot.participant.rijksregisternummer)
    if (!rrnValidation.isValid)
      return
  }

  const formattedRrn = slot.noRrn ? null : formatRijksregisternummer(slot.participant.rijksregisternummer)

  // Mark up-front so concurrent triggers can't double-save the same slot.
  autoSavedSlotIds.add(slot.id)

  const attendeeData: CreateUserAttendeeInput = {
    firstName: slot.participant.first_name,
    lastName: slot.participant.last_name,
    dateOfBirth: slot.participant.date_of_birth || '',
    rijksregisternummer: formattedRrn || null,
    gender: slot.participant.gender || null,
    medicalInfo: slot.participant.medical_info || null,
    allergies: slot.participant.allergies || null,
    emergencyContactName: slot.participant.emergency_contact_name || null,
    emergencyContactPhone: slot.participant.emergency_contact_phone || null,
    savedFormResponses: buildSavedFormResponses(slot),
    isPrimary: false,
    nickname: null,
  }

  try {
    const savePromise = createAttendee(attendeeData)

    trackAttendeeSave(savePromise)

    const result = await savePromise

    if (result.success && result.data) {
      slot.savedAttendee = result.data
      slot.newAttendeeSelected = false
      syncCartFromSlots()

      // Flush any extra form responses that were entered while the create
      // request was in flight (savedAttendee was still null, so
      // updateFormResponses couldn't schedule a save for them).
      await saveFormResponsesForSlot(slot)

      return
    }

    // Duplicate (same rijksregisternummer already saved): link the existing one
    // so the booking still carries a user_attendee_id.
    const existing = await findByRijksregister(formattedRrn!)
    if (existing.success && existing.data) {
      slot.savedAttendee = existing.data
      slot.newAttendeeSelected = false
      syncCartFromSlots()

      // Persist the extra form responses entered for this slot onto the
      // linked attendee so they're not lost.
      await saveFormResponsesForSlot(slot)

      return
    }

    // Save failed for another reason: allow a later retry.
    autoSavedSlotIds.delete(slot.id)
  }
  catch {
    // Auto-save is a convenience, not critical — allow a later retry.
    autoSavedSlotIds.delete(slot.id)
  }
}

// Debounced trigger: persist a new attendee once its basic info (name + valid
// rijksregisternummer) is complete.
watch(slots, () => {
  if (!authStore.user)
    return

  for (const slot of slots.value) {
    if (!slot || slot.savedAttendee || autoSavedSlotIds.has(slot.id))
      continue

    const rrnDigits = String(slot.participant.rijksregisternummer || '').replace(/\D/g, '')
    if (!slot.participant.first_name || !slot.participant.last_name || (!slot.noRrn && rrnDigits.length !== 11))
      continue

    const existingTimer = autoSaveTimers.get(slot.id)
    if (existingTimer)
      clearTimeout(existingTimer)

    autoSaveTimers.set(slot.id, setTimeout(() => {
      autoSaveTimers.delete(slot.id)

      const current = slots.value.find(s => s.id === slot.id)
      if (current)
        autoSaveNewAttendee(current)
    }, 1500))
  }
}, { deep: true })

onUnmounted(() => {
  autoSaveTimers.forEach(timer => clearTimeout(timer))
  autoSaveTimers.clear()
  formSaveTimers.forEach(timer => clearTimeout(timer))
  formSaveTimers.clear()
})

const onSlotTypeChange = (slotIdx: number) => {
  const slot = slots.value[slotIdx]
  if (!slot)
    return
  if (slot.type === 'week')
    slot.selectedDays = []
  syncCartFromSlots()
}

const toggleDay = (slotIdx: number, date: string) => {
  const slot = slots.value[slotIdx]
  if (!slot)
    return
  const idx = slot.selectedDays.indexOf(date)

  // Block adding a day that has no available spots (unless availability is ignored via invite)
  if (idx < 0 && !props.ignoreAvailability) {
    const spotsLeft = dayAvailabilityMap.value[date]
    if (spotsLeft !== undefined && spotsLeft <= 0)
      return
  }

  if (idx >= 0)
    slot.selectedDays.splice(idx, 1)
  else
    slot.selectedDays.push(date)

  // If every available day is now selected, automatically convert to a full week
  // (only when week tickets are available)
  if (availableDays.value.length > 0 && slot.selectedDays.length === availableDays.value.length && !isWeekSoldOut.value) {
    slot.type = 'week'
    slot.selectedDays = []
  }

  syncCartFromSlots()
}

const updateFormResponses = (slotIdx: number, responses: FormResponse[]) => {
  const slot = slots.value[slotIdx]
  if (!slot)
    return
  slot.participant.form_responses = responses
  syncCartFromSlots()

  // Debounced auto-save of extra form responses for already-saved attendees,
  // so changes made after the attendee was created are persisted too.
  if (authStore.user && slot.savedAttendee) {
    const existingTimer = formSaveTimers.get(slot.id)
    if (existingTimer)
      clearTimeout(existingTimer)

    formSaveTimers.set(slot.id, setTimeout(() => {
      formSaveTimers.delete(slot.id)

      const current = slots.value.find(s => s.id === slot.id)
      if (current)
        saveFormResponsesForSlot(current)
    }, 1500))
  }
}

// Slot helpers
const getSlotDisplayName = (slot: GodmodeSlot) => {
  const p = slot.participant
  if (p.first_name && p.last_name)
    return `${p.first_name} ${p.last_name}`
  if (p.first_name)
    return p.first_name

  return 'Nog niet ingevuld'
}

const isSlotComplete = (slot: GodmodeSlot) => {
  const rrn = String(slot.participant.rijksregisternummer || '').replace(/\D/g, '')
  const rrnOk = slot.noRrn || rrn.length === 11

  return !!slot.participant.first_name
    && !!slot.participant.last_name
    && rrnOk
    && (slot.type === 'week' || slot.selectedDays.length > 0)
}

const getSlotStatusColor = (slot: GodmodeSlot) => {
  if (isSlotComplete(slot))
    return 'success'
  if (slot.type === 'day')
    return 'info'

  return 'warning'
}

const getSlotPrice = (slot: GodmodeSlot) => {
  if (slot.type === 'week')
    return weekTicketType.value?.price ?? 0

  return (dayTicketType.value?.price ?? 0) * slot.selectedDays.length
}

const getUnavailableIds = (slotIdx: number): string[] => {
  return slots.value
    .filter((_, i) => i !== slotIdx)
    .map(s => s.participant.rijksregisternummer?.replace(/\D/g, '') || '')
    .filter(Boolean)
}

const hasParticipantData = (slot: GodmodeSlot) => {
  return !!slot.participant.first_name || !!slot.participant.last_name || !!slot.participant.rijksregisternummer
}

const handleHeaderClick = (slotIdx: number) => {
  const slot = slots.value[slotIdx]
  if (!slot || !hasParticipantData(slot))
    return
  slot.isExpanded = !slot.isExpanded
}

// Load ticket types from camp API
const loadTicketTypes = async () => {
  if (!cartStore.camp_id)
    return
  loadingTicketTypes.value = true
  ticketTypeError.value = null
  try {
    const response = await getPublicTicketTypes(cartStore.camp_id, cartStore.invite_token)
    if (response.data) {
      const wt = response.data.find(t => t.type === 'week')
      const dt = response.data.find(t => t.type === 'day')

      weekTicketType.value = wt
        ? { id: wt.id, name: wt.name, price: Number(wt.price), availableQuantity: wt.available_quantity ?? null }
        : null
      dayTicketType.value = dt
        ? { id: dt.id, name: dt.name, price: Number(dt.price), availableQuantity: dt.available_quantity ?? null }
        : null
    }

    // Fetch real-time per-day / week availability (best-effort, failures are non-fatal)
    try {
      const inviteParam = cartStore.invite_token ? `?invite_token=${encodeURIComponent(cartStore.invite_token)}` : ''

      const availResponse = await $fetch<{
        success: boolean
        data: {
          weekTicketAvailability: WeekTicketAvailabilityInfo[]
          dayAvailability: DayAvailabilityInfo[]
        }
      }>(`/api/camps/${cartStore.camp_id}/availability${inviteParam}`)

      if (availResponse.success)
        availabilityData.value = availResponse.data
    }
    catch {
      // Availability data is optional for UI hints — silently ignore.
    }
  }
  catch {
    ticketTypeError.value = 'Kon tickettypes niet laden.'
  }
  finally {
    loadingTicketTypes.value = false
  }
}

// Overall completion check
const allSlotsComplete = computed(() => {
  return slots.value.length > 0 && slots.value.every(isSlotComplete) && cartStore.allFormsComplete
})

const incompleteSlotsCount = computed(() => {
  return slots.value.filter(s => !isSlotComplete(s)).length
})

// If week tickets are sold out, convert any week slots to day so the user
// cannot accidentally proceed with an unavailable ticket type.
// Skipped when ignoreAvailability is true (invite-based booking bypasses capacity).
const fixSoldOutSlots = () => {
  if (!isWeekSoldOut.value || props.ignoreAvailability)
    return

  let changed = false
  for (const slot of slots.value) {
    if (slot.type === 'week') {
      slot.type = 'day'
      slot.selectedDays = []
      changed = true
    }
  }
  if (changed)
    syncCartFromSlots()
}

onMounted(async () => {
  await loadTicketTypes()
  initFromCart()
  fixSoldOutSlots()
  syncCartFromSlots()
})

// Re-initialise when camp_id becomes available after invite hydration.
// Cart2GodmodeFlow mounts before cart.vue's onMounted runs hydrateFromInvite(),
// so camp_id is null on first mount. This watcher catches the delayed set.
watch(() => cartStore.camp_id, async (newId, oldId) => {
  if (newId && !oldId) {
    await loadTicketTypes()
    initFromCart()
    fixSoldOutSlots()
    syncCartFromSlots()
  }
})
</script>

<template>
  <!-- ─── PHASE 1: Ticket-type selection ─────────────────────────────────── -->
  <template v-if="phase === 1">
    <!-- Loading ticket types -->
    <div v-if="loadingTicketTypes" class="d-flex align-center gap-2 mb-4">
      <VProgressCircular indeterminate size="18" width="2" color="primary" />
      <span class="text-body-2 text-medium-emphasis">Tickettypes laden...</span>
    </div>

    <VAlert v-else-if="ticketTypeError" type="error" variant="tonal" density="compact" class="mb-4">
      {{ ticketTypeError }}
      <template #append>
        <VBtn variant="text" color="error" size="x-small" @click="loadTicketTypes">
          Opnieuw
        </VBtn>
      </template>
    </VAlert>

    <template v-else>
      <!-- Count control -->
      <VCard variant="outlined" class="mb-4 pa-3">
        <div class="d-flex align-center gap-4 flex-wrap">
          <div>
            <div class="text-body-1 font-weight-bold mb-0">
              Aantal deelnemers
            </div>
          </div>
          <div class="d-flex align-center gap-2 ms-auto">
            <VBtn
              icon variant="tonal" color="primary" size="small"
              :disabled="slots.length <= 1 || disabled"
              @click="setParticipantCount(slots.length - 1)"
            >
              <VIcon icon="bx-minus" size="18" />
            </VBtn>
            <div class="text-h5 font-weight-bold px-2" style="min-inline-size: 2rem; text-align: center">
              {{ slots.length }}
            </div>
            <VBtn
              icon variant="tonal" color="primary" size="small"
              :disabled="slots.length >= 20 || disabled"
              @click="setParticipantCount(slots.length + 1)"
            >
              <VIcon icon="bx-plus" size="18" />
            </VBtn>
          </div>
        </div>
      </VCard>

      <!-- Per-child ticket type cards -->
      <div class="d-flex flex-column gap-3">
        <VCard
          v-for="(slot, slotIdx) in slots"
          :key="slot.id"
          variant="outlined"
          class="overflow-hidden"
          :disabled="disabled"
        >
          <VCardText class="pa-3">
            <div class="d-flex align-center gap-3 mb-3">
              <VAvatar :color="slot.type === 'week' ? 'warning' : 'info'" variant="tonal" size="36">
                <span class="text-body-2 font-weight-bold">{{ slotIdx + 1 }}</span>
              </VAvatar>
              <div class="text-body-1 font-weight-bold">
                Kind {{ slotIdx + 1 }}
              </div>
              <div class="ms-auto text-body-1 font-weight-bold text-warning">
                €{{ getSlotPrice(slot).toFixed(2) }}
              </div>
            </div>

            <!-- Type toggle -->
            <div class="type-toggle-grid">
              <VCard
                :variant="slot.type === 'week' ? 'tonal' : 'outlined'"
                :color="slot.type === 'week' ? 'warning' : undefined"
                class="type-toggle-card"
                :class="{ selected: slot.type === 'week', disabled: isWeekSoldOut && !props.ignoreAvailability }"
                @click="(isWeekSoldOut && !props.ignoreAvailability) ? null : (slot.type = 'week', onSlotTypeChange(slotIdx))"
              >
                <div class="d-flex align-center gap-3 pa-3">
                  <VIcon icon="bx-calendar-week" size="22" :color="slot.type === 'week' ? 'warning' : undefined" />
                  <div class="text-start">
                    <div class="text-body-2 font-weight-bold">
                      Hele week
                    </div>
                    <div v-if="isWeekSoldOut && !props.ignoreAvailability" class="text-caption text-error">
                      Uitverkocht
                    </div>
                    <div v-else-if="weekTicketType" class="text-caption text-medium-emphasis">
                      €{{ weekTicketType.price.toFixed(2) }} / persoon
                      <template v-if="!props.ignoreAvailability && weekSpotsLeft !== null && weekSpotsLeft <= 10">
                        &bull; <span class="text-warning">nog {{ weekSpotsLeft }} plek{{ weekSpotsLeft !== 1 ? 'ken' : '' }}</span>
                      </template>
                    </div>
                    <div v-else class="text-caption text-disabled">
                      Niet beschikbaar
                    </div>
                  </div>
                  <VIcon
                    v-if="slot.type === 'week'"
                    icon="bx-check-circle"
                    size="20"
                    color="warning"
                    class="ms-auto"
                  />
                </div>
              </VCard>

              <VCard
                :variant="slot.type === 'day' ? 'tonal' : 'outlined'"
                :color="slot.type === 'day' ? 'info' : undefined"
                class="type-toggle-card"
                :class="{ selected: slot.type === 'day', disabled: !dayTicketType || availableDays.length === 0 }"
                @click="(!dayTicketType || availableDays.length === 0) ? null : (slot.type = 'day', onSlotTypeChange(slotIdx))"
              >
                <div class="d-flex align-center gap-3 pa-3">
                  <VIcon icon="bx-calendar-event" size="22" :color="slot.type === 'day' ? 'info' : undefined" />
                  <div class="text-start">
                    <div class="text-body-2 font-weight-bold">
                      Enkele dagen
                    </div>
                    <div v-if="dayTicketType" class="text-caption text-medium-emphasis">
                      €{{ dayTicketType.price.toFixed(2) }} / dag
                    </div>
                    <div v-else class="text-caption text-disabled">
                      Niet beschikbaar
                    </div>
                  </div>
                  <VIcon
                    v-if="slot.type === 'day'"
                    icon="bx-check-circle"
                    size="20"
                    color="info"
                    class="ms-auto"
                  />
                </div>
              </VCard>
            </div>

            <!-- Day picker -->
            <VExpandTransition>
              <div v-if="slot.type === 'day' && availableDays.length > 0" class="mt-3">
                <div class="text-caption text-medium-emphasis mb-2">
                  Selecteer dagen
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <VChip
                    v-for="day in availableDays"
                    :key="day.date"
                    :color="(!props.ignoreAvailability && dayAvailabilityMap[day.date] === 0)
                      ? 'default'
                      : slot.selectedDays.includes(day.date) ? 'info' : 'default'"
                    :variant="slot.selectedDays.includes(day.date) ? 'flat' : 'outlined'"
                    size="small"
                    label
                    class="day-chip"
                    :class="(!props.ignoreAvailability && dayAvailabilityMap[day.date] === 0) ? 'disabled' : 'cursor-pointer'"
                    @click="toggleDay(slotIdx, day.date)"
                  >
                    <VIcon v-if="slot.selectedDays.includes(day.date)" icon="bx-check" start size="14" />
                    {{ day.shortLabel }}
                    <template v-if="!props.ignoreAvailability && dayAvailabilityMap[day.date] === 0">
                      &nbsp;<span class="text-disabled text-caption">(vol)</span>
                    </template>
                    <template v-else-if="!props.ignoreAvailability && (dayAvailabilityMap[day.date] ?? -1) < 10 && (dayAvailabilityMap[day.date] ?? -1) >= 0">
                      &nbsp;<span class="text-warning text-caption">({{ dayAvailabilityMap[day.date] ?? '' }})</span>
                    </template>
                  </VChip>
                </div>
                <div v-if="slot.selectedDays.length === 0" class="text-caption text-warning mt-1">
                  Selecteer minstens één dag
                </div>
                <div v-else class="text-caption text-info mt-1">
                  {{ slot.selectedDays.length }} dag{{ slot.selectedDays.length !== 1 ? 'en' : '' }} geselecteerd
                </div>
              </div>
            </VExpandTransition>
          </VCardText>
        </VCard>
      </div>
    </template>
  </template>

  <!-- ─── PHASE 2: Attendee details ───────────────────────────────────────── -->
  <template v-else>
    <!-- Loading ticket types -->
    <div v-if="loadingTicketTypes" class="d-flex align-center gap-2 mb-4">
      <VProgressCircular indeterminate size="18" width="2" color="primary" />
      <span class="text-body-2 text-medium-emphasis">Tickettypes laden...</span>
    </div>

    <VAlert v-else-if="ticketTypeError" type="error" variant="tonal" density="compact" class="mb-4">
      {{ ticketTypeError }}
      <template #append>
        <VBtn variant="text" color="error" size="x-small" @click="loadTicketTypes">
          Opnieuw
        </VBtn>
      </template>
    </VAlert>

    <template v-else>
      <!-- Slot cards -->
      <div class="d-flex flex-column gap-3">
        <VCard
          v-for="(slot, slotIdx) in slots"
          :key="slot.id"
          class="godmode-slot-card overflow-hidden"
          :disabled="disabled"
        >
          <div class="d-flex">
            <!-- Accent bar -->
            <div
              class="ticket-accent-bar"
              :class="`bg-${getSlotStatusColor(slot)}`"
            />

            <div class="flex-grow-1 ticket-bg">
              <!-- Card header (always visible) -->
              <div
                class="pa-3 cursor-pointer"
                @click="handleHeaderClick(slotIdx)"
              >
                <div class="d-flex align-center gap-3">
                  <!-- Status avatar -->
                  <VAvatar
                    :color="getSlotStatusColor(slot)"
                    variant="tonal"
                    size="36"
                    class="flex-shrink-0"
                  >
                    <VIcon
                      v-if="isSlotComplete(slot)"
                      icon="bx-check"
                      size="18"
                    />
                    <VIcon
                      v-else
                      icon="bx-user"
                      size="18"
                    />
                  </VAvatar>

                  <!-- Middle: name + ticket chips -->
                  <div class="flex-grow-1" style="overflow: hidden; min-inline-size: 0">
                    <div class="d-flex align-center gap-2 flex-wrap">
                      <span class="text-body-2 font-weight-bold text-truncate">
                        {{ hasParticipantData(slot) ? getSlotDisplayName(slot) : `Deelnemer ${slotIdx + 1}` }}
                      </span>
                      <VChip
                        v-if="slot.type === 'week'"
                        size="x-small"
                        color="warning"
                        variant="tonal"
                        class="flex-shrink-0"
                      >
                        <VIcon icon="bx-calendar-week" start size="12" />
                        Hele week
                      </VChip>
                      <template v-else>
                        <VChip
                          size="x-small"
                          :color="slot.selectedDays.length > 0 ? 'info' : 'warning'"
                          variant="tonal"
                          class="flex-shrink-0"
                        >
                          <VIcon icon="bx-calendar-event" start size="12" />
                          {{ slot.selectedDays.length > 0 ? `${slot.selectedDays.length} dag${slot.selectedDays.length !== 1 ? 'en' : ''}` : 'Geen dagen' }}
                        </VChip>
                        <VChip
                          v-for="date in [...slot.selectedDays].sort()"
                          :key="date"
                          size="x-small"
                          color="info"
                          variant="outlined"
                          class="flex-shrink-0"
                        >
                          {{ availableDays.find(d => d.date === date)?.shortLabel || date }}
                        </VChip>
                      </template>
                    </div>
                  </div>

                  <!-- Right: actions + chevron -->
                  <div class="d-flex align-center gap-1 flex-shrink-0">
                    <template v-if="hasParticipantData(slot)">
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        color="primary"
                        @click.stop="pickerRefs[slotIdx]?.openPicker()"
                      >
                        <VIcon icon="bx-refresh" size="16" />
                        <VTooltip activator="parent">
                          Andere deelnemer kiezen
                        </VTooltip>
                      </VBtn>
                      <VBtn
                        icon
                        variant="text"
                        size="x-small"
                        color="error"
                        @click.stop="onAttendeeClear(slotIdx)"
                      >
                        <VIcon icon="bx-unlink" size="16" />
                        <VTooltip activator="parent">
                          Loskoppelen
                        </VTooltip>
                      </VBtn>
                    </template>
                    <VBtn
                      v-else
                      variant="flat"
                      color="primary"
                      size="small"
                      @click.stop="pickerRefs[slotIdx]?.openPicker()"
                    >
                      <VIcon icon="bx-user-plus" start size="16" />
                      Koppel deelnemer
                    </VBtn>
                    <VIcon
                      v-if="hasParticipantData(slot)"
                      :icon="slot.isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'"
                      size="24"
                      color="secondary"
                    />
                  </div>
                </div>
              </div>

              <!-- Expandable content -->
              <VExpandTransition>
                <div v-show="slot.isExpanded" class="px-3 pb-3">
                  <!-- SavedAttendeePicker: hidden visually, rendered for dialog access -->
                  <div class="picker-hidden">
                    <SavedAttendeePicker
                      :ref="(el: unknown) => { if (el) pickerRefs[slotIdx] = el as { openPicker: () => void } }"
                      :model-value="slot.savedAttendee"
                      :has-selection="hasParticipantData(slot)"
                      :disabled-ids="getUnavailableIds(slotIdx)"
                      @select="onAttendeeSelected(slotIdx, $event)"
                      @update:model-value="(val) => { if (!val) onAttendeeClear(slotIdx) }"
                      @new="startNewAttendee(slotIdx)"
                    />
                  </div>

                  <!-- New attendee form fields -->
                  <VCard v-if="slot.newAttendeeSelected || !!slot.savedAttendee || hasParticipantData(slot)" variant="plain">
                    <VCardText class="pb-0">
                      <div class="text-subtitle-2 font-weight-medium mb-3">
                        Basisgegevens
                      </div>
                      <VRow dense>
                        <VCol cols="12" sm="6">
                          <VTextField
                            :model-value="slot.participant.first_name"
                            label="Voornaam *"
                            @update:model-value="val => { slot.participant.first_name = String(val); syncCartFromSlots() }"
                          />
                        </VCol>
                        <VCol cols="12" sm="6">
                          <VTextField
                            :model-value="slot.participant.last_name"
                            label="Achternaam *"
                            @update:model-value="val => { slot.participant.last_name = String(val); syncCartFromSlots() }"
                          />
                        </VCol>
                        <VCol cols="12" sm="6">
                          <VTextField
                            :model-value="slot.participant.rijksregisternummer"
                            label="Rijksregisternummer"
                            maxlength="15"
                            inputmode="numeric"
                            pattern="\d*"
                            :disabled="slot.noRrn"
                            @update:model-value="val => updateRrn(slotIdx, String(val))"
                          />
                          <VCheckbox
                            v-model="slot.noRrn"
                            density="compact"
                            hide-details
                            class="mt-1"
                            @update:model-value="(checked) => {
                              if (checked) {
                                slot.participant.rijksregisternummer = ''
                              }
                              syncCartFromSlots()
                            }"
                          >
                            <template #label>
                              <span class="text-caption">Geen Belgisch rijksregisternummer</span>
                            </template>
                          </VCheckbox>
                        </VCol>
                        <VCol cols="12" sm="6">
                          <VSelect
                            :model-value="slot.participant.gender || ''"
                            label="Geslacht"
                            clearable
                            :items="[
                              { title: 'Meisje', value: 'meisje' },
                              { title: 'Jongen', value: 'jongen' },
                              { title: 'Anders', value: 'x' },
                              { title: 'Zeg ik liever niet', value: '?' },
                            ]"
                            item-title="title"
                            item-value="value"
                            @update:model-value="val => { slot.participant.gender = val || ''; syncCartFromSlots() }"
                          />
                        </VCol>
                      </VRow>
                    </VCardText>
                  </VCard>

                  <!-- Camp forms -->
                  <CheckoutAttendeeForm
                    v-if="campForms.length > 0 && (slot.savedAttendee || slot.newAttendeeSelected)"
                    :forms="(campForms as any)"
                    :participant-index="slotIdx"
                    :participant-name="`${slot.participant.first_name} ${slot.participant.last_name}`"
                    :model-value="slot.participant.form_responses"
                    @update:model-value="updateFormResponses(slotIdx, $event)"
                  />
                </div>
              </VExpandTransition>
            </div>
          </div>
        </VCard>
      </div>

      <!-- Status summary -->
      <VAlert
        v-if="allSlotsComplete"
        type="success"
        variant="tonal"
        class="mt-4"
      >
        <template #prepend>
          <VIcon icon="bx-check-circle" />
        </template>
        <div class="text-body-2">
          Alle {{ slots.length }} deelnemer{{ slots.length !== 1 ? 's zijn' : ' is' }} volledig ingevuld. Je kunt doorgaan naar betaling.
        </div>
      </VAlert>

      <VAlert
        v-else-if="incompleteSlotsCount > 0"
        type="warning"
        variant="tonal"
        class="mt-4"
      >
        <template #prepend>
          <VIcon icon="bx-error-circle" />
        </template>
        <div class="text-body-2">
          {{ incompleteSlotsCount }} deelnemer{{ incompleteSlotsCount !== 1 ? 's zijn' : ' is' }} nog niet volledig ingevuld.
        </div>
      </VAlert>
    </template>
  </template>
</template>

<style lang="scss" scoped>
.godmode-slot-card {
  overflow: hidden;
  border-radius: 0.5rem;
}

.picker-hidden {
  position: absolute;
  overflow: hidden;
  block-size: 0;
  inline-size: 0;
  pointer-events: none;
  visibility: hidden;
}

.ticket-accent-bar {
  flex-shrink: 0;
  inline-size: 4px;
}

.ticket-bg {
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.day-chip {
  transition: all 0.15s ease;
  user-select: none;
}

.type-toggle-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.type-toggle-card {
  border: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  &:not(.selected, .disabled):hover {
    border-color: rgb(var(--v-theme-primary));
    background-color: rgba(var(--v-theme-primary), 0.04);
  }

  &.selected {
    border-color: currentcolor;
    box-shadow: 0 0 0 1px currentcolor;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}
</style>
